import { Operations, ServiceGridItemObject } from '@it2go/types';
import { ServiceTypeEnum } from '@libs/types/src/services/enum/service-type.enum';
import { ServicesDashboardWidgetsEnum } from '@libs/types/src/services/enum/services-dashboard-widgets.enum';
import {
    SERVICE_SETTINGS_CALENDAR_TYPE_KEY,
    SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY,
    SERVICE_SETTINGS_MAIL_TYPE_KEY,
    SERVICE_SETTINGS_MOVEMENT_RESTRICTION_TYPE_KEY,
    SERVICE_SETTINGS_NURSING_ALLOWANCE_TYPE_KEY,
    SERVICE_SETTINGS_SANCTION_TYPE_KEY,
    SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY,
    SERVICE_SETTINGS_SOCIAL_WORK_KEY,
    SERVICE_SETTINGS_SUGGESTION_TYPE_KEY,
    SERVICE_SETTINGS_TASKS_TYPE_KEY,
    SOCIAL_SERVICE_SETTINGS_PAYMENT_TYPE_KEY,
} from '@libs/types/src/services/types/common-service-settings.type';
import { SERVICE_SETTINGS_MEDICATION_TYPE_KEY } from '@libs/types/src/services/types/service-settings-medication.type';
import { SERVICE_OPERATIONAL_REPORTS_KEY } from '@libs/types/src/services/types/service-settings-operational-report.type';
import { SERVICE_SETTINGS_VOUCHER_TYPE_KEY } from '@libs/types/src/services/types/service-settings-voucher.type';
import { NURSING_ALLOWANCE_EXPERTISE_CODES } from '@libs/types/src/services/utils/nursing-allowance';
import { featureOn } from '../../../../feature-flag';
import { Rules } from '../../../../store/types/acl.types';
import { ServiceDashboardWidgetPolicy } from './service-dashboard-widgets.types';

const socialWorkCreateOperations: readonly Operations[] = [
    'work.create',
    'individualWork.create',
    'workGroupActivity.create',
    'workMassWork.create',
];

const healthCare = [ServiceTypeEnum.HEALTH_CARE] as const;
const social = [ServiceTypeEnum.SOCIAL] as const;
const healthCareAndSocial = [ServiceTypeEnum.HEALTH_CARE, ServiceTypeEnum.SOCIAL] as const;

export const SERVICE_DASHBOARD_WIDGET_POLICIES: Readonly<
    Record<ServicesDashboardWidgetsEnum, ServiceDashboardWidgetPolicy>
> = {
    [ServicesDashboardWidgetsEnum.RISKY_CLIENTS]: {
        serviceTypes: healthCare,
        requiredAllOperations: ['folder.read'],
    },
    [ServicesDashboardWidgetsEnum.LOW_MEDICATION]: {
        serviceTypes: healthCare,
        settingKeys: [SERVICE_SETTINGS_MEDICATION_TYPE_KEY],
        requiredAllOperations: ['folderMedication.read'],
    },
    [ServicesDashboardWidgetsEnum.TODO_LIST]: {
        serviceTypes: healthCareAndSocial,
        requiredAllOperations: ['serviceDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.CELEBRATIONS]: {
        serviceTypes: healthCareAndSocial,
        requiredAllOperations: ['folder.read'],
    },
    [ServicesDashboardWidgetsEnum.NEAR_END_VOUCHERS]: {
        serviceTypes: healthCare,
        settingKeys: [SERVICE_SETTINGS_VOUCHER_TYPE_KEY],
        requiredAllOperations: ['voucherDeadline.read'],
        expertiseCodes: ['902', '913', '925'],
    },
    [ServicesDashboardWidgetsEnum.SANCTION]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SANCTION_TYPE_KEY],
        requiredAllOperations: ['workSanction.read'],
    },
    [ServicesDashboardWidgetsEnum.MOVEMENT_RESTRICTIONS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_MOVEMENT_RESTRICTION_TYPE_KEY],
        requiredAllOperations: ['workMovementRestriction.read'],
    },
    [ServicesDashboardWidgetsEnum.INDIVIDUAL_PLAN]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_INDIVIDUAL_PLAN_TYPE_KEY],
        requiredAllOperations: ['individualPlanDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.USER_TASKS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_TASKS_TYPE_KEY],
        requiredAllOperations: ['task.read'],
    },
    [ServicesDashboardWidgetsEnum.MAIL_RECORDS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_MAIL_TYPE_KEY],
        requiredAllOperations: ['folderMail.read'],
    },
    [ServicesDashboardWidgetsEnum.SUGGESTION]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SUGGESTION_TYPE_KEY],
        requiredAllOperations: ['workSuggestion.read'],
    },
    [ServicesDashboardWidgetsEnum.CLIENTS_WITHOUT_RECENT_WORK]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SOCIAL_WORK_KEY],
        requiredAnyOperations: socialWorkCreateOperations,
    },
    [ServicesDashboardWidgetsEnum.FINANCIAL_BALANCE]: {
        serviceTypes: social,
        settingKeys: [SOCIAL_SERVICE_SETTINGS_PAYMENT_TYPE_KEY],
        requiredAllOperations: ['financialBalanceDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.PLANNED_WORK]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_CALENDAR_TYPE_KEY, SERVICE_SETTINGS_SOCIAL_WORK_KEY],
        requiredAllOperations: ['calendar.read'],
        requiredAnyOperations: socialWorkCreateOperations,
    },
    [ServicesDashboardWidgetsEnum.PERFORMED_WORK]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SOCIAL_WORK_KEY],
        requiredAnyOperations: socialWorkCreateOperations,
    },
    [ServicesDashboardWidgetsEnum.WORKED_HOURS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SOCIAL_WORK_KEY],
        requiredAllOperations: ['workedHoursDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.DEBTORS]: {
        serviceTypes: social,
        settingKeys: [SOCIAL_SERVICE_SETTINGS_PAYMENT_TYPE_KEY],
        requiredAllOperations: ['billing.create'],
    },
    [ServicesDashboardWidgetsEnum.WORKERS_WORKED_HOURS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SOCIAL_WORK_KEY],
        requiredAllOperations: ['workersWorkedHoursDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.OPERATIONAL_REPORTS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_OPERATIONAL_REPORTS_KEY],
        requiredAllOperations: ['serviceDashboard.read', 'operationalWork.read'],
    },
    [ServicesDashboardWidgetsEnum.USER_MESSAGES]: {
        serviceTypes: social,
        requiredAllOperations: ['serviceDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS]: {
        serviceTypes: social,
        settingKeys: [SERVICE_SETTINGS_SERVICE_ANNOUNCEMENT_TYPE_KEY],
        requiredAllOperations: ['serviceAnnouncement.read', 'serviceAnnouncementDashboard.read'],
    },
    [ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_DURATION]: {
        serviceTypes: healthCare,
        settingKeys: [SERVICE_SETTINGS_NURSING_ALLOWANCE_TYPE_KEY],
        requiredAllOperations: ['nursingAllowance.read'],
        expertiseCodes: NURSING_ALLOWANCE_EXPERTISE_CODES,
    },
    [ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_CONFIRMATION]: {
        serviceTypes: healthCare,
        settingKeys: [SERVICE_SETTINGS_NURSING_ALLOWANCE_TYPE_KEY],
        requiredAllOperations: ['nursingAllowance.read'],
        expertiseCodes: NURSING_ALLOWANCE_EXPERTISE_CODES,
    },
};

export function isServiceDashboardWidgetFeatureEnabled(
    widgetName: ServicesDashboardWidgetsEnum,
): boolean {
    switch (widgetName) {
        case ServicesDashboardWidgetsEnum.MAIL_RECORDS:
            return featureOn.SOCIAL_JANUARY;
        case ServicesDashboardWidgetsEnum.SUGGESTION:
            return featureOn.SUGGESTION;
        case ServicesDashboardWidgetsEnum.USER_MESSAGES:
            return featureOn.USER_MESSAGES;
        case ServicesDashboardWidgetsEnum.SERVICE_ANNOUNCEMENTS:
            return featureOn.SERVICE_ANNOUNCEMENT;
        case ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_DURATION:
        case ServicesDashboardWidgetsEnum.NURSING_ALLOWANCE_CONFIRMATION:
            return featureOn.NURSING_ALLOWANCE;
        case ServicesDashboardWidgetsEnum.FINANCIAL_BALANCE:
        case ServicesDashboardWidgetsEnum.DEBTORS:
            return featureOn.SOCIAL_PAYMENTS_AND_BILLING;
        case ServicesDashboardWidgetsEnum.PLANNED_WORK:
            return featureOn.NEW_CALENDAR;
        default:
            return true;
    }
}

export function isServiceDashboardWidgetAvailableForService(
    widgetName: ServicesDashboardWidgetsEnum,
    service: ServiceGridItemObject,
): boolean {
    const policy = SERVICE_DASHBOARD_WIDGET_POLICIES[widgetName];
    if (!policy.serviceTypes.includes(service.type as ServiceTypeEnum)) return false;
    if (!isServiceDashboardWidgetFeatureEnabled(widgetName)) return false;
    if (policy.expertiseCodes?.length && !policy.expertiseCodes.includes(service.expertise?.code ?? '')) {
        return false;
    }

    return (policy.settingKeys ?? []).every(settingKey => isServiceSettingEnabled(service, settingKey));
}

export function isServiceDashboardWidgetAllowedByAcl(
    widgetName: ServicesDashboardWidgetsEnum,
    serviceId: number,
    rules: Rules,
): boolean {
    const policy = SERVICE_DASHBOARD_WIDGET_POLICIES[widgetName];
    const allAllowed = (policy.requiredAllOperations ?? [])
        .every(operation => isOperationAllowedForService(rules, operation, serviceId));
    const anyAllowed = !policy.requiredAnyOperations?.length
        || policy.requiredAnyOperations.some(operation => isOperationAllowedForService(rules, operation, serviceId));

    return allAllowed && anyAllowed;
}

export function isOperationAllowedForService(
    rules: Rules,
    operation: Operations,
    serviceId: number,
): boolean {
    const rule = rules[operation];
    return !!rule && (rule.serviceIds.has(serviceId) || rule.serviceIds.has(0));
}

function isServiceSettingEnabled(service: ServiceGridItemObject, settingKey: string): boolean {
    const setting = service.serviceSettings.find(item => item.key === settingKey);
    if (!setting) return true;

    try {
        const data = JSON.parse(setting.data) as { useCard?: boolean };
        return data.useCard ?? true;
    } catch {
        return false;
    }
}
